import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { ExpandMore, Receipt } from '@mui/icons-material'
import { Box, Button, Collapse, Divider, IconButton, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { useGetQuoteQuery } from '@/app/services/quotes'
import { useGetTaxRatesQuery } from '@/app/services/taxRates'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectCurrencySymbol, selectOrganisationId } from '@/app/slices/organisationSlice'
import {
    addItemToAdjustmentQueue,
    selectedQuoteStatus,
    selectIsAdjustmentQueueEmpty,
    selectIsCalculatingQuote,
    setSelectedQuoteStatus,
} from '@/app/slices/quoteItemsSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { compareBySecondaryProcessIndex } from '@/common/helpers/secondaryProcessUtilities'
import { useCurrencyFormatter } from '@/common/hooks'
import { fractionToPercentage, getCurrencyFormat, getFormattedPrice, QUOTE_STATUS_ARRAY } from '@/common/utils'
import PriceDetailsDialog from '@/features/quotes/components/PriceDetailsDialog/PriceDetailsDialog'

const classes = {
    pricesSection: {
        width: '250px',
        gap: '12px',
    },
    pricePercentage: {
        fontSize: '14px',
        color: 'text.secondary',
    },
    bold: {
        fontWeight: 'bold',
    },
    itemColumn: {
        display: 'flex',
        flex: 1,
    },
    alignRight: {
        justifyContent: 'flex-end',
    },
    inputColumn: {
        pl: 2,
    },
    fixedPriceInput: {
        '& .MuiOutlinedInput-root.Mui-disabled': {
            '& .MuiInputBase-input.Mui-disabled': {
                textFillColor: (theme) => theme.palette.text.primary,
            },
            '& fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
            },
        },
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const ItemPrices = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const { quoteId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const currencySymbol = useSelector(selectCurrencySymbol)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)
    const isAdjustmentQueueEmpty = useSelector(selectIsAdjustmentQueueEmpty)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const { data: selectedQuote, isFetching: isFetchingQuote } = useGetQuoteQuery({ organisationId, quoteId })
    const quoteNotCalculated = useSelector(selectedQuoteStatus) !== QUOTE_STATUS_ARRAY.Calculated

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const { data: taxRates } = useGetTaxRatesQuery({ organisationId, params: { includeDeleted: true } })

    const {
        bevelCutPricePerItem,
        bevelCutSetupPricePerItem,
        foldingTotalPricePerItem,
        isOtherChargePercentage,
        itemPrice,
        linePrice,
        materialPrice,
        otherChargePercentage,
        otherChargePrice,
        processingTotalPrice,
        secondaryProcessTotalPricePerItem,
        secondaryProcesses,
    } = quoteItem

    const sortedSecondaryProcesses = useMemo(
        () => [...(secondaryProcesses ?? [])].sort(compareBySecondaryProcessIndex),
        [secondaryProcesses]
    )

    const showSecondaryProcessRows =
        !(quoteNotCalculated && isAdjustmentQueueEmpty) &&
        !isCalculatingQuote &&
        !isFetchingQuote &&
        Boolean(secondaryProcessTotalPricePerItem) &&
        sortedSecondaryProcesses.length > 0

    // null until the user toggles it, so the default follows the number of processes as the item loads
    const [isBreakdownToggledOpen, setIsBreakdownToggledOpen] = useState(null)
    const isBreakdownOpen = isBreakdownToggledOpen ?? sortedSecondaryProcesses.length >= 2

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)
    const hasFixedPrice = Boolean(quoteItem?.partLibraryEntry?.fixedPrice)
    const isFixedPriceExpired = useMemo(() => {
        if (!quoteItem?.partLibraryEntry?.fixedPriceEndTime) return false
        const currentDate = new Date().setHours(0, 0, 0, 0)
        const fixedPriceEndTime = new Date(quoteItem?.partLibraryEntry?.fixedPriceEndTime).setHours(0, 0, 0, 0)
        return currentDate > fixedPriceEndTime
    }, [quoteItem?.partLibraryEntry?.fixedPriceEndTime])

    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const placeholder = getFormattedPrice(0, currencyCode, locale, true)

    const subtotal = useMemo(
        () =>
            (materialPrice || 0) +
            (secondaryProcessTotalPricePerItem || 0) +
            (processingTotalPrice || 0) +
            (foldingTotalPricePerItem || 0) +
            (bevelCutPricePerItem || 0) +
            (bevelCutSetupPricePerItem || 0),
        [
            materialPrice,
            secondaryProcessTotalPricePerItem,
            processingTotalPrice,
            foldingTotalPricePerItem,
            bevelCutPricePerItem,
            bevelCutSetupPricePerItem,
        ]
    )

    // In % mode the saved % is the source and the calculation sets the amount from it.
    // Otherwise the amount is what was saved, and the % follows it.
    const savedPercentage = isOtherChargePercentage
        ? (otherChargePercentage ?? 0)
        : subtotal
          ? ((otherChargePrice ?? 0) / subtotal) * 100
          : 0

    // The inputs hold what the user types, and follow the item whenever a recalculation changes its prices
    const savedValues = `${subtotal}|${otherChargePrice}|${otherChargePercentage}|${isOtherChargePercentage}`
    const [adjustment, setAdjustment] = useState({
        savedValues,
        percentage: savedPercentage,
        amount: otherChargePrice,
    })
    if (adjustment.savedValues !== savedValues) {
        setAdjustment({ savedValues, percentage: savedPercentage, amount: otherChargePrice })
    }

    // Both values are sent, matching the backend's rounding, because the queued item is cached straight away
    const queueAdjustment = (percentage, amount, isPercentage) => {
        if (selectedQuote?.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
        }
        setAdjustment({ savedValues, percentage, amount })
        dispatch(
            addItemToAdjustmentQueue({
                ...quoteItem,
                otherChargePrice: amount,
                otherChargePercentage: percentage,
                isOtherChargePercentage: isPercentage,
            })
        )
    }

    const handlePercentageAdjustmentChange = (values, sourceInfo) => {
        if (sourceInfo.source === 'event') {
            const percentage = values.floatValue ?? 0
            queueAdjustment(percentage, Math.round(subtotal * percentage) / 100, true)
        }
    }

    const handleAmountAdjustmentChange = (values, sourceInfo) => {
        if (sourceInfo.source === 'event') {
            const amount = values.floatValue ?? 0
            queueAdjustment(subtotal ? (amount / subtotal) * 100 : 0, amount, false)
        }
    }

    const [priceDetailsData, setPriceDetailsData] = useState(null)

    const displayPriceDetails = (quoteItem) => {
        setPriceDetailsData(quoteItem)
    }

    const hidePriceDetails = () => {
        setPriceDetailsData(null)
    }

    const quoteTaxRate = selectedQuote?.taxRateId
        ? taxRates?.find((taxRate) => taxRate.id === selectedQuote?.taxRateId)?.percentage
        : selectedQuote?.taxRate

    return (
        <>
            <Box
                sx={[
                    {
                        alignItems: 'flex-start',
                        display: 'flex',
                        flex: '1 0 auto',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    },
                    classes.pricesSection,
                ]}
            >
                <ItemRow>
                    <Typography
                        data-testid="quote-item-prices-section-title"
                        sx={classes.bold}
                    >
                        {t('Prices')}
                    </Typography>
                    <Button
                        data-testid="nqp-quote-item-unit-times-details"
                        endIcon={<Receipt />}
                        size="small"
                        variant="text"
                        onClick={() => displayPriceDetails(quoteItem)}
                    >
                        {t('Details')}
                    </Button>
                </ItemRow>

                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-material-price-label"
                        sx={classes.itemColumn}
                    >
                        {t('Material')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !materialPrice
                            ? ''
                            : fractionToPercentage(materialPrice / subtotal, 0)}
                        %
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !materialPrice
                            ? String.fromCharCode(8212)
                            : formatCurrency(materialPrice)}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-cutting-price-label"
                        sx={classes.itemColumn}
                    >
                        {t('Cutting')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !processingTotalPrice
                            ? ''
                            : fractionToPercentage(processingTotalPrice / subtotal, 0)}
                        %
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !processingTotalPrice
                            ? String.fromCharCode(8212)
                            : formatCurrency(processingTotalPrice)}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-folding-price-label"
                        sx={classes.itemColumn}
                    >
                        {t('Folding')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !foldingTotalPricePerItem
                            ? ''
                            : fractionToPercentage(foldingTotalPricePerItem / subtotal, 0)}
                        %
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !foldingTotalPricePerItem
                            ? String.fromCharCode(8212)
                            : formatCurrency(foldingTotalPricePerItem)}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Box sx={[classes.itemColumn, { alignItems: 'center' }]}>
                        <Typography data-testid="nqp-quote-item-secondary-process-price-label">
                            {t('Secondary processes')}
                        </Typography>
                        {showSecondaryProcessRows ? (
                            <IconButton
                                aria-expanded={isBreakdownOpen}
                                aria-label={t('Secondary process breakdown')}
                                size="small"
                                sx={{ p: 0, ml: 0.5 }}
                                onClick={() => setIsBreakdownToggledOpen(!isBreakdownOpen)}
                            >
                                <ExpandMore
                                    fontSize="small"
                                    sx={{ transform: isBreakdownOpen ? 'rotate(180deg)' : 'none' }}
                                />
                            </IconButton>
                        ) : null}
                    </Box>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !secondaryProcessTotalPricePerItem
                            ? ''
                            : fractionToPercentage(secondaryProcessTotalPricePerItem / subtotal, 0)}
                        %
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !secondaryProcessTotalPricePerItem
                            ? String.fromCharCode(8212)
                            : formatCurrency(secondaryProcessTotalPricePerItem)}
                    </Typography>
                </ItemRow>
                <Collapse
                    in={showSecondaryProcessRows ? isBreakdownOpen : null}
                    sx={{ width: '100%' }}
                    unmountOnExit
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {sortedSecondaryProcesses.map((process) => (
                            <Box
                                data-testid="nqp-quote-item-secondary-process-row"
                                key={process.secondaryProcessId}
                                sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pl: 2 }}
                            >
                                <Typography sx={[classes.itemColumn, classes.pricePercentage]}>
                                    {process.secondaryProcess?.name}
                                </Typography>
                                <Typography sx={[classes.pricePercentage, classes.alignRight]}>
                                    {formatCurrency((process.pricePerItem || 0) + (process.setupPricePerItem || 0))}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
                <Divider
                    orientation="horizontal"
                    flexItem
                />
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-subtotal-label"
                        sx={[classes.itemColumn, classes.bold]}
                    >
                        {t('Subtotal')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight, classes.bold]}>
                        100%
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.bold, classes.alignRight]}>
                        {(quoteNotCalculated && isAdjustmentQueueEmpty) ||
                        isCalculatingQuote ||
                        isFetchingQuote ||
                        !subtotal
                            ? String.fromCharCode(8212)
                            : formatCurrency(subtotal)}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography data-testid="nqp-quote-item-adjustment-label">{t('Adjust')}</Typography>
                    <TbxTooltip
                        title={
                            isPartFromLibrary && hasFixedPrice && !isFixedPriceExpired
                                ? t(
                                      'The adjustment is the difference between the fixed part price and the current calculated price'
                                  )
                                : ''
                        }
                        arrow
                    >
                        <span>
                            <NumericFormat
                                allowNegative={true}
                                customInput={TextField}
                                decimalScale={2}
                                decimalSeparator={currencyFormat.decimal || ','}
                                disabled={
                                    (quoteNotCalculated && isAdjustmentQueueEmpty) ||
                                    (isPartFromLibrary && hasFixedPrice)
                                        ? !isFixedPriceExpired
                                        : null
                                }
                                inputProps={{
                                    style: {
                                        fontSize: '14px',
                                        textAlign: 'right',
                                        paddingBlock: '6px',
                                        paddingInline: '12px',
                                    },
                                    'data-testid': 'nqp-quote-item-adustment-text',
                                }}
                                placeholder="0"
                                size="small"
                                suffix="%"
                                sx={[
                                    classes.itemColumn,
                                    classes.alignRight,
                                    classes.inputColumn,
                                    hasFixedPrice && !isFixedPriceExpired ? classes.fixedPriceInput : null,
                                ]}
                                thousandSeparator={currencyFormat.group}
                                value={adjustment.percentage}
                                variant="outlined"
                                onValueChange={handlePercentageAdjustmentChange}
                            />
                        </span>
                    </TbxTooltip>

                    <TbxTooltip
                        title={
                            isPartFromLibrary && hasFixedPrice && !isFixedPriceExpired
                                ? t(
                                      'The adjustment is the difference between the fixed part price and the current calculated price'
                                  )
                                : ''
                        }
                        arrow
                    >
                        <span>
                            <NumericFormat
                                allowNegative={true}
                                customInput={TextField}
                                decimalScale={currencyFormat.decimalPlaces}
                                decimalSeparator={currencyFormat.decimal}
                                disabled={
                                    (quoteNotCalculated && isAdjustmentQueueEmpty) ||
                                    (isPartFromLibrary && hasFixedPrice)
                                        ? !isFixedPriceExpired
                                        : null
                                }
                                fixedDecimalScale={true}
                                inputProps={{
                                    style: {
                                        fontSize: '14px',
                                        textAlign: 'right',
                                        paddingBlock: '6px',
                                        paddingInline: '12px',
                                    },
                                }}
                                placeholder={placeholder}
                                prefix={currencyFormat.currencySymbolPosition === 'before' ? currencySymbol : undefined}
                                size="small"
                                suffix={currencyFormat.currencySymbolPosition === 'after' ? currencySymbol : undefined}
                                sx={[
                                    classes.itemColumn,
                                    classes.alignRight,
                                    classes.inputColumn,
                                    hasFixedPrice && !isFixedPriceExpired ? classes.fixedPriceInput : null,
                                ]}
                                thousandSeparator={currencyFormat.group}
                                value={adjustment.amount ?? null}
                                variant="outlined"
                                onValueChange={handleAmountAdjustmentChange}
                            />
                        </span>
                    </TbxTooltip>
                </ItemRow>
                <Divider
                    orientation="horizontal"
                    flexItem
                />
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-unit-price-label"
                        sx={[classes.itemColumn, classes.bold]}
                    >
                        {t('Unit price')}
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight, classes.bold]}>
                        {quoteNotCalculated || isCalculatingQuote || isFetchingQuote || !processingTotalPrice
                            ? ''
                            : fractionToPercentage(itemPrice / subtotal, 0)}
                        %
                    </Typography>
                    <Typography sx={[classes.itemColumn, classes.alignRight, classes.bold]}>
                        {quoteNotCalculated || isCalculatingQuote || isFetchingQuote || !itemPrice
                            ? String.fromCharCode(8212)
                            : formatCurrency(itemPrice || 0)}
                    </Typography>
                </ItemRow>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-item-line-price-label"
                        sx={classes.bold}
                    >
                        {t('Line price')}
                    </Typography>
                    <Typography sx={classes.bold}>
                        {quoteNotCalculated || isCalculatingQuote || isFetchingQuote || !linePrice
                            ? String.fromCharCode(8212)
                            : formatCurrency(linePrice || 0)}
                    </Typography>
                </ItemRow>
            </Box>
            {priceDetailsData ? (
                <PriceDetailsDialog
                    open={!!priceDetailsData}
                    quoteItem={priceDetailsData}
                    quoteOverrides={selectedQuote?.quoteOverrides}
                    taxRate={quoteTaxRate}
                    onClose={hidePriceDetails}
                />
            ) : null}
        </>
    )
}

ItemPrices.propTypes = {
    quoteItemId: PropTypes.string,
}

export default memo(ItemPrices)
